/**
 * Inspection Instance Types
 *
 * An instance represents a single execution of a template. Like templates, instances
 * are event-sourced - the projection is computed from an ordered list of events.
 */

import type { SourceEntityIdentifier, SchemaVersion } from './common';
import type { TemplateReference } from './template';

/**
 * Inspection Instance (Projection)
 *
 * This schema represents the RESOLVED instance state - computed from events.
 * Events are embedded in the projection for client-side display and audit.
 *
 * PERSISTENCE: Instances are stored as event streams.
 * PROJECTION: This schema includes both computed state AND source events.
 */
export interface InspectionInstance {
  /** Unique identifier for this instance */
  id: string; // UUID v4

  /** Schema version */
  schemaVersion: SchemaVersion;

  /** Instance version (event count) */
  version: number;

  /** Reference to the source template */
  templateRef: TemplateReference;

  /** Instance metadata (from INSTANCE_CREATED event) */
  metadata: InstanceMetadata;

  /** Custom display name for the instance (optional) */
  customName?: string;

  // ─── Computed State ─────────────────────────────────────────────────

  /** Execution state (derived from events) */
  state: InstanceState;

  /** Responses keyed by item UUID (derived from events) */
  responses: Record<string, ItemResponse>;

  /** Observation items added during execution (derived from events) */
  observations: ObservationItem[];

  /**
   * Count of deficient responses. Resolved on demand server-side from
   * `responses` -- only populated by queries that select it (e.g. the
   * multi-building list, for the Completed cell summary).
   */
  deficienciesCount?: number;

  /**
   * Count of unique service requests linked from responses. Resolved on
   * demand server-side from `responses` -- only populated by queries that
   * select it (e.g. the multi-building list, for the Completed cell summary).
   */
  serviceRequestsCount?: number;

  // ─── Event Log ──────────────────────────────────────────────────────

  /**
   * All events that produced this state.
   * Embedded for:
   * - Client-side audit trail display
   * - Activity timeline / history view
   * - "Who changed what when" queries
   * - Client-side re-projection if needed
   */
  events: InstanceEvent[];
}

export interface InstanceMetadata {
  /** Building/property this inspection is for */
  buildingId: SourceEntityIdentifier;

  /** Tenant this inspection is scoped to (for tenant-specific inspections) */
  tenantId?: SourceEntityIdentifier;

  /** Suite/unit identifier if inspecting specific space */
  suiteId?: SourceEntityIdentifier;

  /** Assigned inspector */
  assignedTo?: string; // User ID

  /** Scheduled execution date/time */
  scheduledFor?: string; // ISO 8601

  /** Schedule reference if from recurring schedule */
  scheduleId?: string;

  /** Creation timestamp */
  createdAt: string; // ISO 8601

  /** User who created the instance */
  createdBy: string; // User ID

  /** Resolved public user who created the instance (populated from API) */
  createdByUser?: PublicUserInfo;
}

/** Minimal user info resolved from PublicUser */
export interface PublicUserInfo {
  id: string;
  name: string;
}

/**
 * Instance State (derived from events)
 *
 * All fields are computed by replaying instance events.
 */
export interface InstanceState {
  /** Current status (derived from lifecycle events) */
  status: InstanceStatus;

  /** When inspection was started (from INSTANCE_STARTED event) */
  startedAt?: string; // ISO 8601

  /** When inspection was completed (from INSTANCE_COMPLETED event) */
  completedAt?: string; // ISO 8601

  /** User who completed the inspection */
  completedBy?: string; // User ID

  /** Resolved public user who completed the inspection (populated from API) */
  completedByUser?: PublicUserInfo;

  /** Overall completion notes (from INSTANCE_COMPLETED event) */
  completionNotes?: string;

  /** Reason for closure (from INSTANCE_CLOSED event) */
  closedReason?: string;

  /** Progress tracking (computed from responses) */
  progress: ProgressState;

  /** Last section/item viewed (from POSITION_UPDATED events) */
  lastPosition?: {
    sectionId: string;
    itemId?: string;
  };
}

export type InstanceStatus =
  | 'scheduled' // Created from schedule, not yet started
  | 'in_progress' // INSTANCE_STARTED received
  | 'completed' // INSTANCE_COMPLETED received
  | 'closed'; // INSTANCE_CLOSED received

export const InstanceStatusEnum: {
  Scheduled: InstanceStatus;
  InProgress: InstanceStatus;
  Completed: InstanceStatus;
  Closed: InstanceStatus;
} = {
  Scheduled: 'scheduled',
  InProgress: 'in_progress',
  Completed: 'completed',
  Closed: 'closed',
};

/** Progress state (computed from responses vs template items) */
export interface ProgressState {
  /** Total items in template */
  totalItems: number;

  /** Items with responses */
  completedItems: number;

  /** Required items total */
  requiredItems: number;

  /** Required items with responses */
  completedRequiredItems: number;

  /** Per-section progress */
  sectionProgress: Record<
    string,
    {
      total: number;
      completed: number;
    }
  >;
}

/**
 * Instance Events
 *
 * All mutations to an instance are captured as events.
 * The projection is computed by replaying events in order.
 */
export type InstanceEvent =
  // Lifecycle
  | {
      type: 'INSTANCE_CREATED';
      payload: CreateInstancePayload;
      timestamp: string;
      userId: string;
    }
  | {
      type: 'INSTANCE_STARTED';
      payload: Record<string, never>;
      timestamp: string;
      userId: string;
    }
  | {
      type: 'INSTANCE_COMPLETED';
      payload: { notes?: string };
      timestamp: string;
      userId: string;
    }
  | {
      type: 'INSTANCE_CLOSED';
      payload: { reason: string };
      timestamp: string;
      userId: string;
    }

  // Responses
  | {
      type: 'ITEM_RESPONSE';
      payload: { itemId: string; response: ItemResponseValue };
      timestamp: string;
      userId: string;
    }
  | {
      type: 'ITEM_RESPONSE_CLEARED';
      payload: { itemId: string };
      timestamp: string;
      userId: string;
    }
  | {
      type: 'ITEM_ADDITIONAL_FIELDS_UPDATED';
      payload: { itemId: string; additionalFields: Record<string, string> };
      timestamp: string;
      userId: string;
    }

  // Photos
  | {
      type: 'PHOTO_ADDED';
      payload: { itemId: string; photo: PhotoAttachment };
      timestamp: string;
      userId: string;
    }
  | {
      type: 'PHOTO_REMOVED';
      payload: { itemId: string; photoId: string };
      timestamp: string;
      userId: string;
    }
  | {
      type: 'PHOTO_UPLOAD_STATUS_CHANGED';
      payload: {
        itemId: string;
        photoId: string;
        status: PhotoUploadStatus;
        remoteUrl?: string;
        error?: string;
        serverPhotoId?: string;
      };
      timestamp: string;
      userId: string;
    }

  // Notes
  | {
      type: 'NOTE_UPDATED';
      payload: { itemId: string; notes: string };
      timestamp: string;
      userId: string;
    }

  // Observations (ad-hoc items)
  | {
      type: 'OBSERVATION_ADDED';
      payload: ObservationItem;
      timestamp: string;
      userId: string;
    }
  | {
      type: 'OBSERVATION_UPDATED';
      payload: { observationId: string; changes: Partial<ObservationItem> };
      timestamp: string;
      userId: string;
    }
  | {
      type: 'OBSERVATION_REMOVED';
      payload: { observationId: string };
      timestamp: string;
      userId: string;
    }

  // Service request integration
  | {
      type: 'SERVICE_REQUEST_LINKED';
      payload: {
        itemId: string;
        serviceRequestId: string;
        userFriendlyId: string;
        /** When true, mobile projection marks the link as pending API confirmation */
        awaitingApiConfirmation?: boolean;
      };
      timestamp: string;
      userId: string;
    }
  | {
      type: 'SERVICE_REQUEST_LINK_API_SETTLED';
      payload: {
        itemId: string;
        serviceRequestId: string;
        success: boolean;
      };
      timestamp: string;
      userId: string;
    }
  | {
      type: 'SERVICE_REQUEST_UNLINKED';
      payload: { itemId: string };
      timestamp: string;
      userId: string;
    }

  // Position tracking (for resume)
  | {
      type: 'POSITION_UPDATED';
      payload: { sectionId: string; itemId?: string };
      timestamp: string;
      userId: string;
    };

export interface CreateInstancePayload {
  templateId: string;
  templateVersion: number;
  buildingId: SourceEntityIdentifier;
  tenantId?: SourceEntityIdentifier;
  suiteId?: SourceEntityIdentifier;
  assignedTo?: string;
  scheduledFor?: string;
  scheduleId?: string;
  customName?: string;
}

/**
 * API persistence state for a service request link (mobile optimistic linking).
 * Omitted or `synced` means the link is persisted server-side or legacy/local-only.
 */
export enum LinkedServiceRequestApiLinkStatus {
  Pending = 'pending',
  Synced = 'synced',
  Failed = 'failed',
}

/**
 * A service request linked to an inspection item response
 */
export interface LinkedServiceRequest {
  id: string;
  /** Human-readable identifier (e.g. "SR-244") */
  userFriendlyId: string;
  /** Set on mobile when the link mutation has not yet succeeded or has failed */
  apiLinkStatus?: LinkedServiceRequestApiLinkStatus;
}

/**
 * Item Response
 */
export interface ItemResponse {
  /** Reference to the item UUID */
  itemId: string;

  /** Primary response value (matches question type options) */
  value: string | number | string[] | null;

  /** Notes/comments (Remarks/Issues) */
  notes?: string;

  /** Additional fields for complex question types (e.g., condition_action) */
  additionalFields?: Record<string, string>;

  /** Attached photos */
  photos: PhotoAttachment[];

  /** DERIVED: Whether this response indicates a deficiency (derived from question type) */
  isDeficient: boolean;

  /** @deprecated Use serviceRequestIds instead */
  serviceRequestId?: string;

  /** Linked service request IDs (if created from deficiencies) */
  serviceRequestIds?: string[];

  /** Linked service requests with full display metadata */
  serviceRequests?: LinkedServiceRequest[];

  /** Response timestamps */
  timestamps: {
    /** When response was first recorded */
    createdAt: string; // ISO 8601
    /** When response was last modified */
    updatedAt: string; // ISO 8601
    /** Device timestamp (may differ from server) */
    deviceTimestamp: string; // ISO 8601
  };

  /** Sync state */
  syncState: ResponseSyncState;
}

/**
 * Type alias for response values
 */
export type ItemResponseValue = ItemResponse['value'];

export type PhotoUploadStatus = 'pending' | 'uploading' | 'uploaded' | 'failed';

export const PhotoUploadStatusEnum: {
  Pending: PhotoUploadStatus;
  Uploading: PhotoUploadStatus;
  Uploaded: PhotoUploadStatus;
  Failed: PhotoUploadStatus;
} = {
  Pending: 'pending',
  Uploading: 'uploading',
  Uploaded: 'uploaded',
  Failed: 'failed',
};

export interface PhotoAttachment {
  /** Unique identifier for this photo */
  id: string; // UUID v4

  /** Local file path (for offline) */
  localPath?: string;

  /** Remote URL (after upload) */
  remoteUrl?: string;

  /** Thumbnail URL */
  thumbnailUrl?: string;

  /** Upload status */
  uploadStatus: PhotoUploadStatus;

  /** Error message if upload failed */
  uploadError?: string;

  /** Photo metadata */
  metadata: PhotoMetadata;
}

export interface PhotoMetadata {
  /** When photo was taken */
  capturedAt: string; // ISO 8601

  /** GPS coordinates if available */
  location?: {
    latitude: number;
    longitude: number;
    accuracy?: number;
  };

  /** File size in bytes */
  fileSize: number;

  /** MIME type (e.g., "image/jpeg", "image/png", "image/heic") */
  contentType: string;

  /** Image dimensions */
  dimensions?: {
    width: number;
    height: number;
  };

  /** Optional caption */
  caption?: string;
}

export type SyncStatus = 'synced' | 'pending' | 'error';

export const SyncStatusEnum: {
  Synced: SyncStatus;
  Pending: SyncStatus;
  Error: SyncStatus;
} = {
  Synced: 'synced',
  Pending: 'pending',
  Error: 'error',
};

export interface ResponseSyncState {
  /** Sync status */
  status: SyncStatus;

  /** Last sync attempt */
  lastAttempt?: string; // ISO 8601

  /** Error message if failed */
  error?: string;

  /** Retry count */
  retryCount: number;
}

export interface ObservationItem {
  /** Unique identifier */
  id: string; // UUID v4

  /** Section this observation was added to */
  sectionId: string;

  /** Observation description */
  description: string;

  /** Observation response */
  response: ItemResponse;

  /** When observation was added */
  addedAt: string; // ISO 8601

  /** Whether to suggest adding to template */
  suggestAddToTemplate: boolean;
}
